import { isRight } from "fp-ts/lib/Either";
import i18next from "i18next";
import { inject, observer } from "mobx-react";
import { Fragment, Component, SyntheticEvent } from "react";
import { Translation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";

import {
  PostUp,
  PostUpList,
  PostUpListResolver,
  Recipient,
  RecipientListResolver,
  Station,
  StationListResolver,
} from "../../decoders/PostUp.types";
import { base64Decode } from "../../lib/utilities";
import { ILog } from "@ihr-radioedit/inferno-core";
import { getRecipient, getRecipientSubscriptions, getStation, sendSubscriptionLists } from "../../services/PostUp";
import { Button, Container, Spinner } from "../../ui";
import { ButtonKind } from "../../ui";
import type { PageBlockInterface } from "../Block.component";
import "./Newsletter.style.scss";
import { Subscribe } from "./Subscribe.component";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("Unsubscribe");

interface UnsubscribeProps extends PageBlockInterface {
  store?: Store;
}

interface UnsubscribeState {
  unsubscribed: boolean;
  email: string;
  stationLists?: Station[];
  subscriber?: Recipient | null;
  subscriberLists?: PostUpList;
}

@inject("store")
@observer
export class Unsubscribe extends Component<UnsubscribeProps, UnsubscribeState> {
  private saveInProgress = false;
  mounted = false;

  constructor(props: UnsubscribeProps) {
    super(props);

    this.state = {
      unsubscribed: false,
      email: "",
    };
  }

  async componentDidMount() {
    this.mounted = true;
    if (!this.props.store) {
      return;
    }

    const { site, request, api_base_uri } = this.props.store;

    let email = "";
    if (request) {
      email = request && request.query ? base64Decode(request.query.u || "") : "";
    }

    if (site && api_base_uri) {
      const stationData = await getStation({
        slug: site.index.slug,
        base_uri: api_base_uri,
      });

      const validatedStations = StationListResolver.decode(stationData.lists);
      const stationLists = isRight(validatedStations) ? validatedStations.right : [];

      let subscriberLists: PostUpList = [];
      let unsubscribed: boolean = this.state.unsubscribed;

      if (email) {
        const subData = await getRecipient({
          slug: site.index.slug,
          base_uri: api_base_uri || "",
          email,
        });

        const validatedRecipient = RecipientListResolver.decode(subData);
        const subscriber = isRight(validatedRecipient) ? validatedRecipient.right[0] : null;

        if (subscriber) {
          const listData = await getRecipientSubscriptions({
            slug: site.index.slug,
            base_uri: api_base_uri,
            recipientId: subscriber.recipientId,
          });

          const validatedSubscriptions = PostUpListResolver.decode(listData);
          if (isRight(validatedSubscriptions)) {
            subscriberLists = validatedSubscriptions.right;
          }

          if (subscriberLists) {
            subscriberLists = subscriberLists.map((list: PostUp) => {
              if (list) {
                list.status = "UNSUB";
              }
              return list;
            });

            const result = await sendSubscriptionLists({
              slug: site.index.slug,
              base_uri: api_base_uri || "",
              lists: subscriberLists,
            });

            if (result) {
              unsubscribed = true;
            }
          }
        }
      }

      if (this.mounted) {
        this.setState({
          unsubscribed,
          stationLists,
          subscriberLists,
          email,
        });
      }
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  save = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!this.props.store || !this.state.subscriberLists || this.saveInProgress) {
      return;
    }

    this.saveInProgress = true;

    try {
      const { site, api_base_uri } = this.props.store;
      const result = await sendSubscriptionLists({
        slug: site.index.slug,
        base_uri: api_base_uri || "",
        lists: this.state.subscriberLists,
      });

      if (result) {
        log.info("Subscriber data updated");
        toast.info(i18next.t("subscriptions_success"), {
          autoClose: 10000,
          toastId: "subscriptions-update-notify",
        });

        this.saveInProgress = false;
      }
    } catch (err) {
      log.info("An error occurred while updating subscriber data: ", err);
    }
  };

  private changeListStatus = (event: SyntheticEvent) => {
    const target = event.target as HTMLInputElement;

    if (target && this.state.subscriberLists && this.mounted) {
      const subscriberLists: PostUpList = this.state.subscriberLists.map((list: PostUp) => {
        if (list) {
          if (target.value === list.listId.toString()) {
            const status = !target.checked ? "UNSUB" : "NORMAL";
            list.status = status;
          }
        }
        return list;
      });

      this.setState({
        subscriberLists,
      });
    }
  };

  render() {
    if (!this.props.store) {
      return null;
    }

    const { site } = this.props.store;
    const header = !this.state.unsubscribed ? (
      <Translation>{t => <h1 className="headline">{t("subscriptions_unsubscribe_wait")}</h1>}</Translation>
    ) : (
      <Translation>
        {t => (
          <h1 className="headline">
            {t("subscriptions_unsubscribe_success", {
              station: `${site.sections.general?.name}`,
            })}
          </h1>
        )}
      </Translation>
    );

    const optionsHeader = (
      <Translation>
        {t => (
          <div className="header">
            {t("subscriptions_unsubscribe_options", {
              email: `${this.state.email || ""}`,
            })}
          </div>
        )}
      </Translation>
    );

    const showSpinner = !this.state.unsubscribed;

    const stationListsForm = !showSpinner ? (
      <section className="user">
        <form role="form" noValidate={true}>
          <div className="options">
            {optionsHeader}
            <ul className="station-lists">
              {this.state.stationLists &&
                this.state.stationLists.map((list: Station, index: number) => {
                  if (list.publicSignup) {
                    return (
                      <li key={index}>
                        <input
                          type="checkbox"
                          name={`stationsLists[${index}]`}
                          id={`station-list-${list.listId}`}
                          value={list.listId}
                          defaultChecked={Subscribe.checkListStatus(list.listId, this.state.subscriberLists || [])}
                          onChange={this.changeListStatus}
                          aria-labelledby={`station-list-label-${list.listId}`}
                        />
                        <label htmlFor={`station-list-${list.listId}`} id={`station-list-label-${list.listId}`}>
                          {list.friendlyTitle || list.title}
                        </label>
                      </li>
                    );
                  }
                })}
            </ul>
          </div>
          <Translation>
            {t => (
              <div className="button-wrapper">
                <Button className={"save-btn"} kind={ButtonKind.PRIMARY} click={this.save}>
                  {t("subscriptions_save")}
                </Button>
              </div>
            )}
          </Translation>
        </form>
      </section>
    ) : null;

    return (
      <Fragment>
        <Container className="component-newsletter">
          {header}
          <Spinner visible={showSpinner} />
          {stationListsForm}
        </Container>
        <ToastContainer autoClose={5000} position="bottom-center" />
      </Fragment>
    );
  }
}

export default Unsubscribe;
